import React, { useState, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet, ScrollRestoration } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import { LanguageProvider } from './utils/LanguageContext';
import UserManagement from './components/UserManagement';
import MedicineManagement from './components/MedicineManagement';
import RackManagement from './components/RackManagement';
import BatchManagement from './components/BatchManagement';
import SupplierManagement from './components/SupplierManagement';
import PurchaseManagement from './components/PurchaseManagement';
import OrderManagement from './components/OrderManagement';
import PrescriptionManagement from './components/PrescriptionManagement';
import PaymentManagement from './components/PaymentManagement';
import DeliveryManagement from './components/DeliveryManagement';
import Profile from './components/Profile';
import Settings from './components/Settings';
import Help from './components/Help';
import Notifications from './components/Notifications';
import NotFound from './components/NotFound';

import Footer from './components/Footer';
import TopNavigation from './components/TopNavigation';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AuthLayout from './components/AuthLayout';
import { PermissionProvider } from './utils/PermissionContext';
import { ToastProvider } from './components/ToastContext';
import { CartProvider } from './utils/CartContext';
import { CurrencyProvider } from './utils/CurrencyContext';
import PermissionBasedRoute from './components/PermissionBasedRoute';
import AllCustomers from './components/AllCustomers';
import AllDeliveryPersons from './components/AllDeliveryPersons';
import CustomerDashboard from './components/CustomerDashboard';
import MyOrders from './components/MyOrders';
import MyPrescriptions from './components/MyPrescriptions';
import MyPayments from './components/MyPayments';
import RoleManagement from './components/RoleManagement';
import ForgotPassword from './components/ForgotPassword';
import Legal from './components/Legal';
import OfflineSell from './components/OfflineSell';
import DoctorManagement from './components/DoctorManagement';
import AppointmentManagement from './components/AppointmentManagement';
import { ThemeProvider } from './utils/ThemeContext';

import { logout, isTokenExpired, getAuthToken, getRefreshToken } from './utils/api';
import './App.css';
import './components/Toast.css';

const App = () => {
  const getSystemTheme = () => window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const token = getAuthToken();
    const refreshToken = getRefreshToken();
    if (!token && !refreshToken) return false;
    if (token && !isTokenExpired(token)) return true;
    if (refreshToken && !isTokenExpired(refreshToken)) return true;
    return false;
  });
  const [theme, setTheme] = useState(localStorage.getItem('theme') || getSystemTheme());
  const [language, setLanguage] = useState('en');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  const handleLogin = () => {
    setIsLoggedIn(true);
  };

  // Monitor token expiration and auth:logout events
  useEffect(() => {
    const checkTokenStatus = () => {
      const token = getAuthToken();
      const refreshToken = getRefreshToken();

      // If user was logged in but tokens are missing or both are expired
      if (isLoggedIn) {
        if (!token && !refreshToken) {
          setIsLoggedIn(false);
          logout();
        } else if (token && isTokenExpired(token) && (!refreshToken || isTokenExpired(refreshToken))) {
          setIsLoggedIn(false);
          logout();
        }
      }
    };

    const handleAuthLogout = () => {
      setIsLoggedIn(false);
      setShowLogoutModal(false);
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    window.addEventListener('storage', checkTokenStatus);
    const interval = setInterval(checkTokenStatus, 15000); // Check every 15s

    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
      window.removeEventListener('storage', checkTokenStatus);
      clearInterval(interval);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    document.body.className = theme;
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleLogout = () => setShowLogoutModal(true);

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const confirmLogout = () => {
    logout();
    setIsLoggedIn(false);
    setShowLogoutModal(false);
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const closeSidebar = () => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  // AuthRootLayout component for authentication-related routes
  const AuthRootLayout = () => {
    if (isLoggedIn) return <Navigate to="/dashboard" replace />;
    return <AuthLayout><Outlet /></AuthLayout>;
  };

  // RootLayout component for authenticated routes, using Outlet for child content
  const RootLayout = () => {
    if (!isLoggedIn) return <Navigate to="/login" replace />;
    return <RootLayoutContent />;
  };

  // Separate component for authenticated layout
  const RootLayoutContent = () => {
    return (
      <>
        <TopNavigation />
        <div className={`app-container ${theme}`} data-bs-theme={theme}>
          <main className="main-content">
            <div className="content-wrapper">
              <ScrollRestoration />
              <Outlet />
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  };

  const router = createBrowserRouter([
    {
      path: '/login',
      element: <AuthRootLayout />,
      children: [
        { index: true, element: <Login onLogin={handleLogin} /> }
      ]
    },
    {
      path: '/forgot-password',
      element: <AuthRootLayout />,
      children: [
        { index: true, element: <ForgotPassword /> }
      ]
    },
    {
      path: '/',
      element: <RootLayout />,
      children: [
        { path: 'dashboard', element: <Dashboard language={language} /> },
        { path: 'customer-dashboard', element: <CustomerDashboard /> },
        { path: 'my-orders', element: <MyOrders /> },
        { path: 'my-prescriptions', element: <MyPrescriptions /> },
        { path: 'my-payments', element: <MyPayments /> },
        { path: 'users', element: <PermissionBasedRoute element={UserManagement} permission="manage_users" /> },
        { path: 'roles', element: <PermissionBasedRoute element={RoleManagement} permission="manage_roles" /> },
        { path: 'customers', element: <PermissionBasedRoute element={AllCustomers} permission="view_customers" /> },
        { path: 'delivery-persons', element: <PermissionBasedRoute element={AllDeliveryPersons} permission="view_delivery_persons" /> },
        { path: 'medicines', element: <PermissionBasedRoute element={MedicineManagement} permission="manage_medicines" /> },
          { path: 'racks', element: <PermissionBasedRoute element={RackManagement} permission="manage_medicines" /> },
        { path: 'batches', element: <PermissionBasedRoute element={BatchManagement} permission="manage_batches" /> },
        { path: 'suppliers', element: <PermissionBasedRoute element={SupplierManagement} permission="manage_suppliers" /> },
        { path: 'purchases', element: <PermissionBasedRoute element={PurchaseManagement} permission="manage_purchases" /> },
        { path: 'orders', element: <PermissionBasedRoute element={OrderManagement} permission="manage_orders" /> },
        { path: 'prescriptions', element: <PermissionBasedRoute element={PrescriptionManagement} permission="manage_prescriptions" /> },
        { path: 'payments', element: <PermissionBasedRoute element={PaymentManagement} permission="manage_payments" /> },
        { path: 'deliveries', element: <PermissionBasedRoute element={DeliveryManagement} permission="manage_deliveries" /> },
        { path: 'appointments', element: <PermissionBasedRoute element={AppointmentManagement} permission="manage_appointments" /> },
        { path: 'doctors', element: <PermissionBasedRoute element={DoctorManagement} permission="manage_doctors" /> },
        { path: 'offline-sell', element: <PermissionBasedRoute element={OfflineSell} permission="manage_orders" /> },
        { path: 'profile', element: <Profile /> },
        { path: 'settings', element: <PermissionBasedRoute element={Settings} requiredRole="admin" /> },
        { path: 'help', element: <Help /> },
        { path: 'notifications', element: <Notifications /> },
        { path: 'privacy-policy', element: <PrivacyPolicy /> },
        { path: 'terms-of-service', element: <TermsOfService /> },
        { path: 'legal', element: <Legal /> },
        { path: '/', element: <Navigate to="dashboard" replace /> },
        { path: '*', element: <NotFound /> },
      ]
    }
  ]);

  return (
  <div style={{ minHeight: "100vh", backgroundColor: "var(--background-color)" }}>
    <CurrencyProvider>
      <CartProvider>
        <PermissionProvider key={isLoggedIn}>
          <ToastProvider>
            <ThemeProvider theme={theme} setTheme={setTheme}>
              <LanguageProvider language={language} setLanguage={setLanguage}>
                <RouterProvider router={router} />
              </LanguageProvider>

              <Modal
                show={showLogoutModal}
                onHide={() => setShowLogoutModal(false)}
                data-bs-theme={theme}
              >
                <Modal.Header closeButton>
                  <Modal.Title>Confirm Logout</Modal.Title>
                </Modal.Header>
                <Modal.Body className='bg-transparent'>Are you sure you want to log out?</Modal.Body>
                <Modal.Footer className='bg-transparent'>
                  <Button
                    variant="secondary"
                    onClick={() => setShowLogoutModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={confirmLogout}>
                    Logout
                  </Button>
                </Modal.Footer>
              </Modal>
            </ThemeProvider>
          </ToastProvider>
        </PermissionProvider>
      </CartProvider>
    </CurrencyProvider>
  </div>
);
};

export default App;